/**
 * OverlapRemoval — angle-preserving, rectangle-aware overlap relaxation.
 *
 * Used on blur (pane click) to redistribute every revealed node so the
 * canvas reads as a clean constellation again, *without* shuffling the
 * angles each node sits at relative to the current center.
 *
 * For every non-center node we keep its angle θ from the center and
 * push it along that ray by the *minimum AABB clearance* required to
 * stop overlapping any other node. This is the key contrast vs. a
 * circular-radius push: a typical card (~200×60) at angle 135° only
 * needs to slide a few pixels horizontally, not be lifted 100+ px
 * vertically as a bounding-circle would imply.
 *
 * The algorithm never drops nodes — even in fully-packed cycle cases
 * we cap at `maxRadius` and accept residual overlap (logged) so every
 * revealed node stays on screen.
 */

import type { NodeSize, PlacedNode } from './RadialLayoutEngine';

// ============================================================================
// Types
// ============================================================================

export interface RelaxInput {
	/** Center to preserve angles around (graph-space). */
	center: { x: number; y: number };
	/** Optional id of the center; if present, never moved. */
	centerId?: string;
	/** All nodes that should remain visible after relaxation. */
	nodes: Array<{ id: string; x: number; y: number }>;
	/** id → measured size (or estimated default). */
	sizes: Map<string, NodeSize>;
	/** Asymmetric padding — typically `{ x: 16, y: 12 }`. */
	padding?: { x: number; y: number };
	/** Soft cap on how far a single node can be pushed from its initial radius. */
	maxRadius?: number;
	/** Max passes around the node list. */
	iterations?: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PADDING = { x: 16, y: 12 };
const DEFAULT_MAX_RADIUS = 4000;
const DEFAULT_ITERATIONS = 6;
const FALLBACK_SIZE: NodeSize = { width: 200, height: 60 };

// ============================================================================
// Helpers
// ============================================================================

function sizeOr(sizes: Map<string, NodeSize>, id: string): NodeSize {
	return sizes.get(id) ?? FALLBACK_SIZE;
}

/**
 * Minimum scalar `t ≥ 0` such that moving `a` by `t * dir` separates its
 * AABB (half-extents `ha`) from `b`'s AABB (half-extents `hb`) along
 * the ray. Returns 0 if already separated.
 *
 * Math: two AABBs overlap iff `|Δx| < hax + hbx` AND `|Δy| < hay + hby`.
 * Moving `a` by `t * dir` shifts both Δx and Δy by `t * dir.x` and
 * `t * dir.y` respectively. For each axis we want the smallest `t` that
 * exits the slab, taking the side `a` is currently on so we move *away*
 * from `b`. Then `min(tX, tY)` is enough (clearing one axis is enough
 * to separate the rectangles).
 */
function minRayClearance(
	a: { x: number; y: number },
	ha: { x: number; y: number },
	b: { x: number; y: number },
	hb: { x: number; y: number },
	dir: { x: number; y: number },
): number {
	const dx = a.x - b.x;
	const dy = a.y - b.y;

	const overlapX = ha.x + hb.x - Math.abs(dx);
	const overlapY = ha.y + hb.y - Math.abs(dy);
	if (overlapX <= 0 || overlapY <= 0) return 0;

	// X-slab: we need (a.x + t*dir.x - b.x) to satisfy |·| ≥ ha.x + hb.x.
	// We need to move away from `b`. If a.x ≥ b.x, we want dx + t*dir.x ≥
	// ha.x + hb.x, so t ≥ (ha.x + hb.x − dx) / dir.x. We then require
	// dir.x to be > 0 to move further right; otherwise this axis can't
	// separate by going along `dir`.
	let tX = Number.POSITIVE_INFINITY;
	if (Math.abs(dir.x) > 1e-6) {
		const sideX = dx >= 0 ? 1 : -1;
		const dirAlong = sideX * dir.x;
		if (dirAlong > 1e-6) {
			tX = (ha.x + hb.x - sideX * dx) / dirAlong;
			if (tX < 0) tX = 0;
		}
	}

	let tY = Number.POSITIVE_INFINITY;
	if (Math.abs(dir.y) > 1e-6) {
		const sideY = dy >= 0 ? 1 : -1;
		const dirAlong = sideY * dir.y;
		if (dirAlong > 1e-6) {
			tY = (ha.y + hb.y - sideY * dy) / dirAlong;
			if (tY < 0) tY = 0;
		}
	}

	const t = Math.min(tX, tY);
	if (!Number.isFinite(t)) return 0;
	return Math.max(0, t);
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Relax `nodes` around `center` so no two AABBs overlap (within
 * `padding`), preserving each node's angle from the center. Returns a
 * new `Map<id, {x, y}>` with the relaxed positions.
 *
 * Existing positions are read; node identities and angles are kept.
 * Nodes with no clear angle (sitting on top of the center) get a
 * deterministic fallback angle based on a hash of their id so they
 * don't all stack on the same ray.
 */
export function relaxAroundCenter(input: RelaxInput): Map<string, { x: number; y: number }> {
	const {
		center,
		centerId,
		nodes,
		sizes,
		padding = DEFAULT_PADDING,
		maxRadius = DEFAULT_MAX_RADIUS,
		iterations = DEFAULT_ITERATIONS,
	} = input;

	// Fold padding into the half-extents up-front.
	const halfExtents = new Map<string, { x: number; y: number }>();
	for (const node of nodes) {
		const size = sizeOr(sizes, node.id);
		halfExtents.set(node.id, {
			x: size.width / 2 + padding.x,
			y: size.height / 2 + padding.y,
		});
	}

	// Working copies so we don't mutate the caller's array.
	const positions: PlacedNode[] = nodes.map((n) => ({ id: n.id, x: n.x, y: n.y }));
	const positionById = new Map(positions.map((p) => [p.id, p]));

	// Preserve each node's angle and remember its initial radius so we
	// can cap pushes at `maxRadius` further out.
	const angles = new Map<string, number>();
	const initialRadii = new Map<string, number>();
	for (const node of positions) {
		if (node.id === centerId) continue;
		const dx = node.x - center.x;
		const dy = node.y - center.y;
		let r = Math.hypot(dx, dy);
		let angle: number;
		if (r < 1) {
			// Node is on top of the center — pick a stable deterministic
			// fallback angle so the relaxation pass can move it outward
			// instead of stacking it on the same ray.
			angle = (hashIdToAngle(node.id) + 2 * Math.PI) % (2 * Math.PI);
			r = 1;
		} else {
			angle = Math.atan2(dy, dx);
		}
		angles.set(node.id, angle);
		initialRadii.set(node.id, r);
	}

	// Sort nodes inside-out so closer-in nodes get placed first.
	const order = positions
		.filter((p) => p.id !== centerId)
		.map((p) => ({ id: p.id, r: initialRadii.get(p.id) ?? 0 }))
		.sort((a, b) => a.r - b.r);

	for (let pass = 0; pass < iterations; pass++) {
		let anyMoved = false;

		for (const entry of order) {
			const a = positionById.get(entry.id);
			if (!a) continue;
			const ha = halfExtents.get(a.id);
			if (!ha) continue;
			const angle = angles.get(a.id);
			if (angle === undefined) continue;
			const dir = { x: Math.cos(angle), y: Math.sin(angle) };

			// Find the largest required `t` across every other node and
			// advance by that amount along the ray.
			let requiredT = 0;
			for (const b of positions) {
				if (b.id === a.id) continue;
				const hb = halfExtents.get(b.id);
				if (!hb) continue;
				const t = minRayClearance(a, ha, b, hb, dir);
				if (t > requiredT) requiredT = t;
			}
			if (requiredT < 0.5) continue;

			// Cap at maxRadius from the original radius.
			const currentR = Math.hypot(a.x - center.x, a.y - center.y);
			let allowed = requiredT;
			const maxAllowed = maxRadius - currentR;
			if (allowed > maxAllowed) allowed = Math.max(0, maxAllowed);
			if (allowed <= 0) {
				continue;
			}

			a.x += dir.x * allowed;
			a.y += dir.y * allowed;
			anyMoved = true;
		}

		if (!anyMoved) break;
	}

	const out = new Map<string, { x: number; y: number }>();
	for (const node of positions) out.set(node.id, { x: node.x, y: node.y });
	return out;
}

function hashIdToAngle(id: string): number {
	let hash = 0;
	for (let i = 0; i < id.length; i++) {
		hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
	}
	return ((hash % 360) * Math.PI) / 180;
}
